import React, { useEffect, useState, useContext, useMemo } from "react";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import { BarChart } from '@mui/x-charts/BarChart';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { 
  DashboardCardTitle, 
  CHART_MARGINS, 
  MOBILE_CHART_MARGINS, 
  MODERN_CARD_STYLE, 
  CARD_HEADER_STYLE, 
  CARD_CONTENT_STYLE,
  StandardChartCard,
  useResponsiveChartProps,
  getXAxisConfig,
  getYAxisConfig,
  getImpactSeriesConfig,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme, Box, CircularProgress, Typography } from '@mui/material';

const capitalizeWords = (str) =>
  str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const AttributesByImpactChart = ({ selectedAttribute: propSelectedAttribute, setSelectedAttribute: propSetSelectedAttribute }) => {
  // Get business context
  const businessContext = useContext(BusinessContext);
  const selectedBusiness = businessContext?.selectedBusiness;
  const businessCurrencySymbol = businessContext?.currencySymbol;
  // Get selectedBusinesses for turnover lookup
  const selectedBusinesses = businessContext?.selectedBusinesses;

  // Get context values
  const reviewsContext = useContext(ReviewsContext);
  const {
    aggregatedData,
    isAggregatedLoading,
    attributesChartData,
    isAttributesChartLoading,
    fetchAttributesChartData,
    isReviewsLoading,
    isAnalysisLoading,
    selectedSubject,
    setSelectedSubject,
    view = "Sales",
    currencySymbol = businessCurrencySymbol || "$",
    sortOption = "total",
    selectedCategory,
    setSelectedDate,
    setSelectedAttribute: contextSetSelectedAttribute,
    timeframe = "30"
  } = reviewsContext || {};

  // Get numSelected from business context
  const numSelected = businessContext?.selectedBusinesses?.length || 0;

  // Determine final data, prioritizing overrides if provided
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { chartSx, slotProps: responsiveSlotProps, margin } = useResponsiveChartProps();

  const MAX_ATTRIBUTES = 8;

  // Determine which state/setter to use (prop or context)
  const setSelectedAttribute = propSetSelectedAttribute || contextSetSelectedAttribute || (() => {});
  const selectedAttribute = propSelectedAttribute !== undefined ? propSelectedAttribute : reviewsContext?.selectedAttribute;

  // NEW: Get attribute impact data from aggregatedData context
  const attributeImpactDataFromBackend = useMemo(() => {
    if (isAttributesChartLoading || !attributesChartData) {
      return {}; // Return empty object if data is not ready
    }
    // Transform backend data (array of AttributeChartDataItem) into the structure previously used by attributeImpact memo (map)
    const impactMap = {};
    (attributesChartData || []).forEach(item => {
      const lowerKeyAttribute = item.attributeNameOriginalCase.toLowerCase();
      impactMap[lowerKeyAttribute] = {
        // Map fields from AttributeChartDataItem to the structure expected by downstream memos
        originalCase: item.attributeNameOriginalCase,
        positiveRatingSum: item.positiveRatingSum,
        negativeRatingSum: item.negativeRatingSum,
        totalImpactSort: item.totalImpactSort,
        positiveRevenue: item.positiveRevenue,
        negativeRevenue: item.negativeRevenue,
        averagePositiveRating: item.averagePositiveRating,
        averageNegativeRating: item.averageNegativeRating,
        // subjectName: item.subjectNameOriginalCase // If backend provided it and it was needed
      };
    });
    return impactMap;
    // selectedSubject is a dependency because attributesChartData from backend is filtered by it.
  }, [attributesChartData, isAttributesChartLoading, selectedSubject]);

  // --- Calculate sorted attributes and top attributes using useMemo ---
  const { topAttributesRaw, allAttributes } = useMemo(() => {
    const impactData = attributeImpactDataFromBackend; // Use data from backend
    if (Object.keys(impactData).length === 0) {
        return { topAttributesRaw: [], allAttributes: [] }; // Return empty if no impact data
    }

    const sorted = Object.keys(impactData).sort((a, b) => {
      switch (sortOption) {
        case 'positive':
          // Sort by summed positive rating
          return impactData[b].positiveRatingSum - impactData[a].positiveRatingSum;
        case 'negative':
          // Sort by summed negative rating (most negative first)
          return impactData[a].negativeRatingSum - impactData[b].negativeRatingSum;
        case 'total':
        default:
          // Sort by total absolute summed rating
          return impactData[b].totalImpactSort - impactData[a].totalImpactSort;
      }
    });

    // Top attributes (raw lowercase keys)
    const topLower = sorted.slice(0, MAX_ATTRIBUTES);
    // Map back to original case for display state
    const topOriginalCase = topLower.map(lower => impactData[lower].originalCase);

    // All attributes for the Autocomplete (capitalized form from original case)
    const allCaps = sorted.map(lower => capitalizeWords(impactData[lower].originalCase));
    allCaps.sort((a, b) => a.localeCompare(b));

    return { topAttributesRaw: topOriginalCase, allAttributes: allCaps };

  }, [attributeImpactDataFromBackend, sortOption, MAX_ATTRIBUTES]);

  // --- Calculate chart series data using useMemo ---
  const { positiveData, negativeData, chartAttributes } = useMemo(() => {
    const impactData = attributeImpactDataFromBackend; // Use new data source
    let finalRawOriginalCase = [...topAttributesRaw];
    
    const selectedAttributeLower = selectedAttribute?.toLowerCase();
    const impactKeysLower = Object.keys(impactData);

    if (selectedAttribute && 
        impactKeysLower.includes(selectedAttributeLower) && 
        !finalRawOriginalCase.some(attr => attr.toLowerCase() === selectedAttributeLower)) {
        const originalCaseToAdd = impactData[selectedAttributeLower]?.originalCase;
        if (originalCaseToAdd) {
             finalRawOriginalCase.push(originalCaseToAdd); 
        }
    }

    const finalRawLower = finalRawOriginalCase.map(a => a.toLowerCase());

    const posData = [];
    const negData = [];
    finalRawLower.forEach(lower => {
        if (!impactData[lower]) { // Use new data source
            return;
        }
        if (view === "Sales") {
          posData.push(impactData[lower]?.positiveRevenue || 0);
          negData.push(impactData[lower]?.negativeRevenue || 0);
        } else { // Rating View
          posData.push(impactData[lower]?.averagePositiveRating || 0);
          negData.push(impactData[lower]?.averageNegativeRating || 0);
        }
    });

    const result = {positiveData: posData, negativeData: negData, chartAttributes: finalRawOriginalCase};
    return result;

  }, [attributeImpactDataFromBackend, topAttributesRaw, selectedAttribute, view]);

  // --- REMOVED: Individual data fetching to prevent duplicates ---
  // DashboardOverview handles all data fetching coordination
  // useEffect(() => {
  //   if (selectedBusinesses && selectedBusinesses.length > 0 && selectedSubject) {
  //     const businessIds = selectedBusinesses.map(b => b.id);
  //     fetchAttributesChartData(businessIds, timeframe, view, sortOption, selectedCategory, selectedSubject);
  //   }
  // }, [selectedSubject, fetchAttributesChartData, selectedBusinesses, timeframe, view, sortOption, selectedCategory]);

  // Removed useState/useEffect for derived data

  // onAxisClick remains largely the same, uses derived chartAttributes
  const onAxisClick = (event, params) => {
    event.stopPropagation();
    
    const clickedIndex = params?.dataIndex;
    if (clickedIndex !== undefined) {
      const clickedAttribute = chartAttributes[clickedIndex]; // Use derived state
      if (clickedAttribute) {
        if (selectedAttribute === clickedAttribute) {
          setSelectedAttribute(null);
          return;
        }
        setSelectedAttribute(clickedAttribute);
      }
    }
  };

  // Determine overall loading state
  const isLoading = isAttributesChartLoading || (!attributesChartData && (isReviewsLoading || isAnalysisLoading));

  // Combine responsiveSlotProps with custom ones for the chart
  const chartSlotProps = useMemo(() => ({
    ...responsiveSlotProps, // Include props from the hook
    legend: { hidden: true },
    tooltip: { content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={view} /> }
  }), [responsiveSlotProps, currencySymbol, view]);

  let chartTitle = "Impact by Attribute";
  if (selectedSubject) {
    chartTitle = `${capitalizeWords(selectedSubject)} Attributes`;
  } else if (selectedCategory) {
    chartTitle = `${capitalizeWords(selectedCategory)} Attributes`;
  }

  return (
    <StandardChartCard 
      data-chart-type="attribute-chart"
      data-filter-exempt="true"
      title={chartTitle}
      titleIcon={<FormatListBulletedIcon />}
      action={
        <Autocomplete
          options={allAttributes} // Use derived state
          sx={{ 
            width: 120, 
            ml: '10px',
            mr: '10px',
            mt: '10px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Search" 
              variant="outlined" 
              size="small"
              InputProps={{
                ...params.InputProps,
                style: { fontSize: '0.9rem' }
              }}
              InputLabelProps={{
                style: { fontSize: '0.9rem' }
              }}
            />
          )}
          ListboxProps={{
            style: { fontSize: '0.9rem' }
          }}
          slotProps={{ // Keep fixed PaperProps
            paper: {
              style: { fontSize: '0.9rem' }
            }
          }}
          onChange={(e, newValue) => {
            if (e && e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
            if (newValue === selectedAttribute) {
              setSelectedAttribute(null);
            } else {
              setSelectedAttribute(newValue || null);
            } 
          }}
          value={selectedAttribute || null} // Use derived state
          clearOnEscape
        />
      }
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : chartAttributes.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
          <Typography variant="body2" color="textSecondary">No attribute data found for the selected filters.</Typography>
        </Box>
      ) : (
        <BarChart
          sx={chartSx} 
          onAxisClick={onAxisClick}
          xAxis={[getXAxisConfig(chartAttributes, isSmallScreen)]} // Use derived state
          yAxis={[getYAxisConfig(currencySymbol, view, isSmallScreen)]}
          series={getImpactSeriesConfig(positiveData, negativeData, currencySymbol, view)} // Use derived state
          slotProps={chartSlotProps} // Use combined slotProps
          margin={margin} // Use margin from hook
        />
      )}
    </StandardChartCard>
  );
};

export default AttributesByImpactChart;
